/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * Le risposte al questionario "Dicci la tua" (migrazione 016).
 *
 * Non sono task: contano nella somma. In alto la media e come si distribuiscono
 * i voti; sotto le risposte, con il bottone per trasformare in segnalazione
 * quelle che meritano lavoro.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, ArrowRightCircle, Check } from 'lucide-react';
import { feedbackAPI } from '../api/feedback';
import { fmtDataOra } from '../utils/date';

const FILTRI = [
  { id: 'tutti', label: 'Tutte' },
  { id: 'non_letti', label: 'Da leggere' },
  { id: 'con_testo', label: 'Con suggerimenti' },
];

function Stelle({ voto, size = 15 }) {
  return (
    <span style={{ display: 'inline-flex', gap: '1px' }} aria-label={`${voto} su 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} color={n <= voto ? '#F59E0B' : '#D1D5DB'} fill={n <= voto ? '#F59E0B' : 'none'} />
      ))}
    </span>
  );
}

export default function FeedbackClienti({ onApriSegnalazione }) {
  const queryClient = useQueryClient();
  const [filtro, setFiltro] = useState('tutti');
  const [inConversione, setInConversione] = useState(null);
  const [errore, setErrore] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['feedback', filtro],
    queryFn: () => feedbackAPI.list(filtro),
  });

  const aggiorna = () => queryClient.invalidateQueries({ queryKey: ['feedback'] });
  const letto = useMutation({
    mutationFn: ({ id, valore }) => feedbackAPI.segnaLetto(id, valore),
    onSuccess: aggiorna,
  });
  const converti = useMutation({
    mutationFn: ({ id, payload }) => feedbackAPI.inSegnalazione(id, payload),
    onSuccess: (s) => {
      setInConversione(null);
      aggiorna();
      queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
      onApriSegnalazione(s.id);
    },
    onError: (e) => setErrore(e.response?.data?.error || 'Operazione non riuscita'),
  });

  if (isLoading) return <div style={s.vuoto}>Caricamento...</div>;
  if (error) return <div style={s.vuoto}>{error.response?.data?.error || 'Errore nel caricamento'}</div>;

  const st = data.statistiche;
  const massimo = Math.max(1, ...Object.values(st.distribuzione));

  return (
    <div>
      <div style={s.cards}>
        <div style={s.card}>
          <div style={s.cardLabel}>Ti piace l'app? Media</div>
          <div style={s.media}>
            {st.media === null ? '—' : st.media.toFixed(1).replace('.', ',')}
            {st.media !== null && <Stelle voto={Math.round(st.media)} size={18} />}
          </div>
          <div style={s.cardNota}>{st.risposte} risposte in tutto</div>
        </div>
        <div style={s.card}>
          <div style={s.cardLabel}>Ultimi 90 giorni</div>
          <div style={s.media}>{st.media_90 === null ? '—' : st.media_90.toFixed(1).replace('.', ',')}</div>
          <div style={s.cardNota}>{st.risposte_90} risposte: dice se si sta migliorando</div>
        </div>
        <div style={{ ...s.card, flex: '2 1 260px' }}>
          <div style={s.cardLabel}>Come si distribuiscono i voti</div>
          {[5, 4, 3, 2, 1].map((v) => (
            <div key={v} style={s.barraRiga}>
              <span style={{ width: '1.2rem', textAlign: 'right' }}>{v}</span>
              <Star size={12} color="#F59E0B" fill="#F59E0B" />
              <span style={s.barraTraccia}>
                <span style={{ ...s.barraPiena, width: `${(st.distribuzione[v] / massimo) * 100}%` }} />
              </span>
              <span style={{ width: '2rem', textAlign: 'right', color: '#6B7280' }}>{st.distribuzione[v]}</span>
            </div>
          ))}
        </div>
      </div>
      <p style={s.nota}>I numeri non contano gli utenti di prova; l'elenco li mostra, marcati.</p>

      <div style={s.filtri}>
        {FILTRI.map((f) => (
          <button key={f.id} onClick={() => setFiltro(f.id)} style={{ ...s.filtroBtn, ...(filtro === f.id ? s.filtroAttivo : {}) }}>
            {f.label}{f.id === 'non_letti' && st.non_letti > 0 ? ` (${st.non_letti})` : ''}
          </button>
        ))}
      </div>

      {errore && <div style={s.errore}>{errore}</div>}

      <div style={s.elenco}>
        {data.feedback.length === 0 ? (
          <div style={s.vuoto}>Nessuna risposta qui.</div>
        ) : data.feedback.map((f) => (
          <div key={f.id} style={{ ...s.riga, ...(f.letto ? {} : s.rigaNuova) }}>
            <div style={s.rigaTesta}>
              <Stelle voto={f.voto} />
              <span style={s.autore}>{f.autore || '—'}</span>
              <span style={s.ruolo}>{f.ruolo}</span>
              {f.di_prova && <span style={s.prova}>prova</span>}
              <span style={s.data}>{fmtDataOra(f.created_at)}</span>
            </div>
            {f.nuova_funzione && <div style={s.risposta}><strong>Vorrebbe:</strong> {f.nuova_funzione}</div>}
            {f.da_migliorare && <div style={s.risposta}><strong>Da migliorare:</strong> {f.da_migliorare}</div>}
            {!f.nuova_funzione && !f.da_migliorare && <div style={s.soloVoto}>Solo il voto, nessun commento.</div>}
            <div style={s.azioni}>
              {f.segnalazione_id ? (
                <button style={s.link} onClick={() => onApriSegnalazione(f.segnalazione_id)}>
                  <ArrowRightCircle size={14} /> Segnalazione: {f.segnalazione_titolo}
                </button>
              ) : (f.nuova_funzione || f.da_migliorare) && (
                <button style={s.btnPiccolo} onClick={() => {
                  setErrore('');
                  setInConversione({ f, tipo: f.nuova_funzione ? 'funzionalita' : 'miglioramento', titolo: '' });
                }}>
                  <ArrowRightCircle size={14} /> Trasforma in segnalazione
                </button>
              )}
              <button style={s.btnTesto} onClick={() => letto.mutate({ id: f.id, valore: !f.letto })}>
                <Check size={14} /> {f.letto ? 'Segna da leggere' : 'Segna letta'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {inConversione && (
        <div style={s.overlay} onClick={() => setInConversione(null)}>
          <div style={s.modale} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Trasforma in segnalazione</h3>
            <div style={s.citazione}>
              {inConversione.f.nuova_funzione && <div><strong>Vorrebbe:</strong> {inConversione.f.nuova_funzione}</div>}
              {inConversione.f.da_migliorare && <div><strong>Da migliorare:</strong> {inConversione.f.da_migliorare}</div>}
            </div>
            <label style={s.label}>Tipo</label>
            <select style={s.input} value={inConversione.tipo} onChange={(e) => setInConversione({ ...inConversione, tipo: e.target.value })}>
              <option value="funzionalita">Nuova funzionalità</option>
              <option value="miglioramento">Miglioramento</option>
              <option value="bug">Bug</option>
            </select>
            <label style={s.label}>Titolo del task</label>
            <input style={s.input} autoFocus value={inConversione.titolo} maxLength={200}
              placeholder="In poche parole, cosa va fatto"
              onChange={(e) => setInConversione({ ...inConversione, titolo: e.target.value })} />
            <div style={s.hint}>La risposta originale viene copiata nella descrizione. Il cliente non riceve avvisi.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button style={s.btnSecondario} onClick={() => setInConversione(null)}>Annulla</button>
              <button style={s.btnPrimario} disabled={!inConversione.titolo.trim() || converti.isPending}
                onClick={() => converti.mutate({ id: inConversione.f.id, payload: { tipo: inConversione.tipo, titolo: inConversione.titolo } })}>
                {converti.isPending ? 'Creazione...' : 'Crea segnalazione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  cards: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  card: { flex: '1 1 180px', backgroundColor: 'white', borderRadius: '12px', padding: '1rem 1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardLabel: { fontSize: '0.74rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, marginBottom: '0.35rem' },
  cardNota: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.25rem' },
  media: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.6rem', fontWeight: 800, color: '#111827' },
  barraRiga: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', marginTop: '0.2rem' },
  barraTraccia: { flex: 1, height: '9px', backgroundColor: '#F3F4F6', borderRadius: '5px', overflow: 'hidden' },
  barraPiena: { display: 'block', height: '100%', backgroundColor: '#F59E0B', borderRadius: '5px' },
  nota: { fontSize: '0.78rem', color: '#6B7280', margin: '0.6rem 0 0.9rem' },
  filtri: { display: 'inline-flex', backgroundColor: '#E5E7EB', borderRadius: '10px', padding: '3px', marginBottom: '0.9rem', flexWrap: 'wrap' },
  filtroBtn: { border: 'none', background: 'none', padding: '0.45rem 0.9rem', borderRadius: '8px', fontSize: '0.86rem', fontWeight: 600, color: '#4B5563', cursor: 'pointer', minHeight: '36px' },
  filtroAttivo: { backgroundColor: 'white', color: '#7C3AED', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' },
  elenco: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  riga: { backgroundColor: 'white', borderRadius: '12px', padding: '0.9rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', borderLeft: '4px solid transparent' },
  rigaNuova: { borderLeftColor: '#8B5CF6' },
  rigaTesta: { display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap' },
  autore: { fontWeight: 600, fontSize: '0.9rem' },
  ruolo: { fontSize: '0.72rem', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '0.05rem 0.4rem', borderRadius: '4px' },
  prova: { fontSize: '0.72rem', color: '#92400E', backgroundColor: '#FEF3C7', padding: '0.05rem 0.4rem', borderRadius: '4px' },
  data: { marginLeft: 'auto', fontSize: '0.78rem', color: '#6B7280' },
  risposta: { fontSize: '0.88rem', lineHeight: 1.55, marginTop: '0.45rem', whiteSpace: 'pre-wrap' },
  soloVoto: { fontSize: '0.82rem', color: '#9CA3AF', marginTop: '0.4rem' },
  azioni: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.65rem' },
  btnPiccolo: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', backgroundColor: '#F3E8FF', color: '#7C3AED', border: 'none', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  btnTesto: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.5rem', background: 'none', color: '#6B7280', border: 'none', fontSize: '0.8rem', cursor: 'pointer' },
  link: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: 0, background: 'none', border: 'none', color: '#047857', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left' },
  vuoto: { padding: '2rem', textAlign: 'center', color: '#6B7280', backgroundColor: 'white', borderRadius: '12px' },
  errore: { padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '0.8rem' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 },
  modale: { backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', width: '100%', maxWidth: '460px' },
  citazione: { fontSize: '0.84rem', color: '#4B5563', backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '0.6rem 0.75rem', lineHeight: 1.5 },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginTop: '0.75rem', marginBottom: '0.25rem' },
  input: { width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.92rem', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white' },
  hint: { fontSize: '0.76rem', color: '#6B7280', marginTop: '0.5rem' },
  btnPrimario: { padding: '0.55rem 1rem', backgroundColor: '#8B5CF6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  btnSecondario: { padding: '0.55rem 1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
};
